import { ReactNode, useEffect, useMemo, useState } from 'react';
import createSafeContext from '@paradigm/utils/createSafeContext';
import { useObservable } from '@paradigm/utils/src/hooks';

import { DrfqOrdersStore } from '#/unified-rfqs/store/drfq-orders';
import { DrfqOrderbooksStore } from '#/unified-rfqs/store/drfq-orderbooks';

import { createDrfqService, DrfqService } from '#/unified-rfqs/services/drfq';
import {
  CallbackHandlers,
  createDrfqWsService,
  DrfqWsService,
} from '#/unified-rfqs/services/drfq-ws';
import {
  createDrfqBboService,
  DrfqBboService,
} from '#/unified-rfqs/services/drfq-bbo';
import { DrfqHistoryService } from '#/unified-rfqs/services/drfq-history';
import { DrfqDraftsService } from '#/unified-rfqs/services/drfq-drafts';
import { Order, RfqOrder } from '#/unified-rfqs/entities/drfq/orders';
import { Trade, TradeTapeItem } from '#/unified-rfqs/entities/shared';
import { Trade as DrfqTrade } from '#/unified-rfqs/entities/drfq/trades';
import DrfqStatusUpdater from '#/unified-rfqs/services/drfq-status-updater';

import { Drfq } from '#/unified-rfqs/entities/drfq/domain';
import { DrfqHistoryStore } from '#/unified-rfqs/store/drfq-history';
import { useNotificationHandlerActions } from '#/unified-rfqs/ui/notifications/notifications-handler-context';
import { useUnifiedMarketsNotifications } from '#/unified-rfqs/ui/notifications/notifications-context';
import DrfqNotificationsService from '#/unified-rfqs/services/drfq-notifications';
import { useOrder } from '#/unified-rfqs/contexts/unified-orders';
import { useTrade } from '#/unified-rfqs/contexts/unified-trades';
import { useTradeTapeItem } from '#/unified-rfqs/contexts/unified-trade-tapes';
import RfqsStore from '#/unified-rfqs/store/rfqs';
import OrdersStore from '#/unified-rfqs/store/orders';
import TradesStore from '#/unified-rfqs/store/trades';
import TradeTapeStore from '#/unified-rfqs/store/trade-tape';
import { DrfqDraftsStore } from '#/unified-rfqs/store/drfq-drafts';

interface Dependencies {
  readonly orderbooksStore: DrfqOrderbooksStore;
  readonly drfqStatusUpdater: DrfqStatusUpdater;
  readonly drfqOrdersStore: DrfqOrdersStore;
  readonly drfqHistoryStore: DrfqHistoryStore;
  readonly drfqDraftsStore: DrfqDraftsStore;
  readonly bboService: DrfqBboService;
  readonly wsService: DrfqWsService;
  readonly service: DrfqService;
  readonly drfqHistoryService: DrfqHistoryService;
  readonly drfqDraftsService: DrfqDraftsService;
  readonly pinRfq: (rfqId: string | number) => void;
  readonly tradeTapeStore: TradeTapeStore;
}

const [useDrfqDependencies, DrfqDependenciesCtxProvider] =
  createSafeContext<Dependencies>('DrfqDependencies');

export function useDrfqService() {
  return useDrfqDependencies().service;
}

export function useDrfqWsStatus() {
  return useObservable(useDrfqDependencies().wsService.wsStatus);
}

export function useDrfqOrderbookStore() {
  return useDrfqDependencies().orderbooksStore;
}

export function useDrfqHistoryService() {
  return useDrfqDependencies().drfqHistoryService;
}

export function useDrfqHistoryStore() {
  return useDrfqDependencies().drfqHistoryStore;
}

export function useDrfqDraftsStore() {
  return useDrfqDependencies().drfqDraftsStore;
}

export function useDrfqDraftsService() {
  return useDrfqDependencies().drfqDraftsService;
}

export function useDrfqBboService() {
  return useDrfqDependencies().bboService;
}

export function useDrfqOrdersStore(rfqId: string) {
  return useDrfqDependencies().drfqOrdersStore.byRfq(rfqId);
}

export function useDrfqOrders(rfqId: string) {
  const drfqOrdersStore = useDrfqOrdersStore(rfqId);
  const ob = useMemo(() => drfqOrdersStore.list(), [drfqOrdersStore]);
  return useObservable(ob);
}

export interface DrfqHandlers {
  readonly onError: (message: string) => void;
  readonly onTradeCompleted: (trade: Trade) => void;
  readonly onDrfqClosed: (drfq: Drfq) => void;
  readonly onDrfqOrderCanceled: (order: Order) => void;
  /** Optional — only fires for async-settlement venues (Paradex). */
  readonly onTradeSubmitted?: (trade: DrfqTrade) => void;
}
interface Props {
  readonly children: ReactNode;
  readonly handlers: DrfqHandlers;
  readonly pinRfq: (rfqId: string | number) => void;
  readonly drfqOrderbooksStore: DrfqOrderbooksStore;
  readonly drfqOrdersStore: DrfqOrdersStore;
  readonly drfqHistoryStore: DrfqHistoryStore;
  readonly drfqDraftsStore: DrfqDraftsStore;
  readonly rfqStore: RfqsStore;
  readonly ordersStore: OrdersStore;
  readonly tradesStore: TradesStore;
  readonly tradeTapeStore: TradeTapeStore;
}

export function DrfqDependenciesProvider({
  children,
  handlers,
  pinRfq,
  drfqOrderbooksStore,
  drfqOrdersStore,
  drfqHistoryStore,
  drfqDraftsStore,
  rfqStore,
  ordersStore,
  tradesStore,
  tradeTapeStore,
}: Props) {
  const { fireNotification } = useUnifiedMarketsNotifications();
  const { handleRfqReceived, handleRfqUpdated, storeUserTrade } =
    useNotificationHandlerActions();

  const drfqHandlers = useMemo(
    () => ({
      ...handlers,
      handleRfqReceived,
      handleRfqUpdated,
      storeUserTrade,
    }),
    [handleRfqReceived, handleRfqUpdated, handlers, storeUserTrade],
  );
  const [deps, setDeps] = useState<Dependencies>();

  const drfqNotificationService = useMemo(
    () =>
      new DrfqNotificationsService({
        fireNotification,
        pinRfq,
        orderbooksStore: drfqOrderbooksStore,
      }),
    [fireNotification, drfqOrderbooksStore, pinRfq],
  );

  useEffect(() => {
    const [_deps, cleanup] = createDependencies(
      drfqHandlers,
      pinRfq,
      drfqOrderbooksStore,
      drfqOrdersStore,
      drfqHistoryStore,
      drfqDraftsStore,
      drfqNotificationService,
      rfqStore,
      ordersStore,
      tradesStore,
      tradeTapeStore,
    );
    setDeps(_deps);
    return cleanup;
  }, [
    drfqHandlers,
    drfqNotificationService,
    fireNotification,
    handlers,
    drfqOrderbooksStore,
    drfqOrdersStore,
    pinRfq,
    drfqHistoryStore,
    drfqDraftsStore,
    rfqStore,
    ordersStore,
    tradesStore,
    tradeTapeStore,
  ]);

  // Currently don't need a spinner as deps is initialized immediately.
  // If we had async code we would need _loading_ and _error_ views here.
  if (deps == null) return null;

  return (
    <DrfqDependenciesCtxProvider value={deps}>
      {children}
    </DrfqDependenciesCtxProvider>
  );
}

/**
 * Creates all dependencies specific for DRFQs
 *
 * Work in progress: currently we are in the process of removing global
 * instances in favor of local instantiation bound to the application
 * lifecycle. This new approach removes possibilities of bugs caused
 * by global state persisting for the whole lifetime of the app and
 * due to instantiation being done as a side effect of import.
 *
 * @param handlers
 * @param pinRfq
 * @param orderbooksStore
 * @param drfqOrdersStore
 * @param drfqHistoryStore
 * @param drfqNotificationService
 * @returns dependencies and a cleanup function to free resources
 */
function createDependencies(
  handlers: CallbackHandlers,
  pinRfq: (rfqId: string | number) => void,
  orderbooksStore: DrfqOrderbooksStore,
  drfqOrdersStore: DrfqOrdersStore,
  drfqHistoryStore: DrfqHistoryStore,
  drfqDraftsStore: DrfqDraftsStore,
  drfqNotificationService: DrfqNotificationsService,
  rfqStore: RfqsStore,
  ordersStore: OrdersStore,
  tradesStore: TradesStore,
  tradeTapeStore: TradeTapeStore,
): [deps: Dependencies, cleanup: () => void] {
  const bboService = createDrfqBboService();
  const drfqStatusUpdater = new DrfqStatusUpdater(rfqStore);
  const wsService = createDrfqWsService({
    pinRfq,
    channels: ['rfqs', 'rfq_orders', 'orders', 'trades', 'trade_tape'],
    rfqStore,
    orderbooksStore,
    drfqOrdersStore,
    drfqHistoryStore,
    ordersStore,
    tradesStore,
    tradeTapeStore,
    drfqNotificationService,
    handlers,
  });
  const service = createDrfqService({
    orderbooksStore,
    drfqOrdersStore,
    drfqHistoryStore,
    drfqDraftsStore,
    tradeTapeStore,
    rfqStore,
    ordersStore,
    tradesStore,
  });

  const drfqHistoryService = new DrfqHistoryService({
    drfqService: service,
    drfqHistoryStore,
  });

  const drfqDraftsService = new DrfqDraftsService({
    drfqDraftsStore,
    drfqService: service,
  });

  const deps: Dependencies = {
    orderbooksStore,
    drfqOrdersStore,
    drfqHistoryStore,
    drfqDraftsStore,
    drfqStatusUpdater,
    drfqHistoryService,
    drfqDraftsService,
    bboService,
    tradeTapeStore,
    wsService,
    service,
    pinRfq,
  };

  const cleanup = () => {
    wsService.teardown();
    bboService.teardown();
    drfqStatusUpdater.teardown();
    drfqHistoryStore.clear();
    drfqDraftsStore.clear();
    drfqOrdersStore.clear();
    orderbooksStore.clear();
  };

  return [deps, cleanup];
}

export function useDrfqOrderbook(
  rfqId: Drfq['id'],
): [bids: readonly RfqOrder[], asks: readonly RfqOrder[]] {
  const drfqOrderbook = useDrfqOrderbookStore().byRfq(rfqId);

  const bids = useObservable(drfqOrderbook.bySide('BUY').list());
  const asks = useObservable(drfqOrderbook.bySide('SELL').list());

  return [bids, asks];
}

export function useDrfqOrder(orderId: Order['id']) {
  const order = useOrder(orderId);

  if (order.rfqType !== 'drfq') {
    throw new TypeError(`Order ${order.id} is not of kind drfq`);
  }

  return order;
}

export function useDrfqTrade(tradeId: Trade['id']) {
  const trade = useTrade(tradeId);

  if (trade.rfqType !== 'drfq') {
    throw new TypeError(`Trade ${tradeId} is not of kind drfq`);
  }

  return trade;
}

export function useDrfqTradeTapeItem(tradeId: TradeTapeItem['id']) {
  const trade = useTradeTapeItem(tradeId);

  if (trade.rfqType !== 'drfq') {
    throw new TypeError(`Trade tape item ${tradeId} is not of kind drfq`);
  }

  return trade;
}

/**
 * ATTENTION: This hook should be used only in places in which
 * the DRFQ is guaranteed to exist.
 *
 */
export function useDrfqHistoryItem(rfqId: Drfq['id']) {
  const drfqHistoryStore = useDrfqHistoryStore();
  const drfq = useObservable(drfqHistoryStore.item(rfqId));

  if (drfq == null) {
    throw new TypeError(`Drfq ${rfqId} does not exist`);
  }

  return drfq;
}

export function useMaybeDrfqHistoryItem(rfqId: Drfq['id']) {
  const drfqHistoryStore = useDrfqHistoryStore();
  return useObservable(drfqHistoryStore.item(rfqId));
}

export function useDrfqHistoryFetchData() {
  const drfqHistorySvc = useDrfqHistoryService();
  return useObservable(drfqHistorySvc.fetcher.fetchData);
}

export function useDrfqHistoryIdsList() {
  const { fetcher } = useDrfqHistoryService();

  const [drfqIds, setDrfqIds] = useState(fetcher.selectPaginatedIds());

  useEffect(() => {
    return fetcher.subscribePaginatedIds(setDrfqIds);
  }, [fetcher]);

  return drfqIds;
}

export function useDrfqDraftsItem(rfqId: Drfq['id']) {
  const drfqDraftsStore = useDrfqDraftsStore();
  const drfq = useObservable(drfqDraftsStore.item(rfqId));

  if (drfq == null) {
    throw new TypeError(`Drfq ${rfqId} does not exist`);
  }

  return drfq;
}

export function useMaybeDrfqDraftsItem(rfqId: Drfq['id']) {
  const drfqDraftsStore = useDrfqDraftsStore();
  return useObservable(drfqDraftsStore.item(rfqId));
}

export function useDrfqDraftsFetchData() {
  const drfqDraftsSvc = useDrfqDraftsService();
  return useObservable(drfqDraftsSvc.fetcher.fetchData);
}

export function useDrfqDraftsIdsList() {
  const { fetcher } = useDrfqDraftsService();

  const [drfqIds, setDrfqIds] = useState(fetcher.selectPaginatedIds());

  useEffect(() => {
    return fetcher.subscribePaginatedIds(setDrfqIds);
  }, [fetcher]);

  return drfqIds;
}

export function useDrfqDraftOrHistoryItem(
  rfqId: Drfq['id'],
  showDrafts: boolean,
) {
  const drfqDraftsStore = useDrfqDraftsStore();
  const drfqHistoryStore = useDrfqHistoryStore();

  const store = showDrafts ? drfqDraftsStore : drfqHistoryStore;
  const item = useObservable(store.item(rfqId));

  if (!item) {
    throw new Error(`DRFQ item not found: ${rfqId}`);
  }

  return item;
}
